import { MainLayout } from "@/components/MainLayout";
import { SEO } from "@/components/SEO";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { TrendingUp, Users, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMemo, useState } from "react";
import { useTopClientsByRevenue } from "@/hooks/reports/useTopClientsByRevenue";

export default function TopClientsByRevenue() {
  const [topN, setTopN] = useState("10");

  // Fetch real data via unified reportsApi + React Query
  const params = useMemo(() => ({ limit: Number(topN) || 10 }), [topN]);
  const { data: topClients = [] } = useTopClientsByRevenue(params);

  const clients = topClients.map(c => ({
    name: c.client_name,
    revenue: c.revenue,
    transactions: c.transactions,
    percentage: 0,
    trend: 'stable' as const,
  }));


  const totalRevenue = clients.reduce((sum, c) => sum + c.revenue, 0);

  return (
    <MainLayout>
      <SEO title="Top Clientes por Receita" description="Ranking de clientes por receita gerada" />
      <div className="space-y-6">
        <div className="section-delimited">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink asChild><Link to="/reports">Relatórios</Link></BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink asChild><Link to="/reports/receivables">Contas a Receber</Link></BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Top Clientes por Receita</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="mt-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">Top Clientes por Receita</h1>
              <p className="text-muted-foreground">Ranking dos principais clientes geradores de receita</p>
            </div>
            <div className="flex gap-2">
              <Select value={topN} onValueChange={setTopN}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">Top 5</SelectItem>
                  <SelectItem value="10">Top 10</SelectItem>
                  <SelectItem value="20">Top 20</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline">Exportar</Button>
            </div>
          </div>
        </div>

        <div className="section-delimited">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="card-elevated">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Receita Total</CardDescription>
                  <DollarSign className="h-4 w-4 text-finance-green" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">R$ {totalRevenue.toLocaleString('pt-BR')}</div>
                <p className="text-xs text-muted-foreground mt-1">Top {topN} clientes</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Representatividade</CardDescription>
                  <TrendingUp className="h-4 w-4 text-finance-blue" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">87.9%</div>
                <p className="text-xs text-muted-foreground mt-1">Da receita total</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Ticket Médio</CardDescription>
                  <Users className="h-4 w-4 text-finance-purple" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">R$ 5.520</div>
                <p className="text-xs text-muted-foreground mt-1">Por transação</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="section-delimited">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-lg">Ranking de Clientes</CardTitle>
              <CardDescription>Ordenado por receita gerada no período</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {clients.map((client, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-medium">{client.name}</div>
                        <div className="text-sm text-muted-foreground">{client.transactions} transações</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="font-bold text-lg">R$ {client.revenue.toLocaleString('pt-BR')}</div>
                        <div className="text-sm text-muted-foreground">{client.percentage}% do total</div>
                      </div>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${client.trend === 'up' ? 'bg-green-100 text-green-600' : client.trend === 'down' ? 'bg-red-100 text-red-600' : 'bg-card text-foreground'}`}>
                        {client.trend === 'up' ? '↑' : client.trend === 'down' ? '↓' : '→'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
